<script lang="ts">
	import type { Season } from '#lib/schema-extension.js';
	import { getTeamImageUrl } from '../hockeydata.js';
	import type { TableCol } from '../types/util/TableCol.js';
	import Table from '#lib/components/Table.svelte';
	import { goto } from '$app/navigation';
	import type {
		HDStandingsResponseData,
		HDStandingsResponseRow
	} from '../types/responses/Standings.js';
	import Placeholder from '#lib/components/Placeholder.svelte';
	import { getStandings } from '../hockeydata.remote.js';
	import { convertSlug } from '#lib/util.js';

	interface HDStandingsProps {
		season?: Season;
		teamId?: string;
		title?: string | null;
	}

	let { season, teamId, title }: HDStandingsProps = $props();

	const standings = $derived.by(() => {
		if (!season) {
			return Promise.reject('No season provided');
		}
		return getStandings(season);
	});

	const standingsCols: TableCol[] = [
		{
			key: 'tableRank',
			label: '#',
			sortable: true,
			tooltip: 'Platzierung',
			tooltipClass: 'tooltip-right'
		},
		{
			key: 'teamShortname',
			label: 'Team',
			sortable: true,
			tooltip: 'Team',
			class: 'justify-start'
		},
		{
			key: 'gamesPlayed',
			label: 'GP',
			sortable: true,
			tooltip: 'Gespielte Spiele'
		},
		{
			key: 'gamesWon',
			label: 'W',
			sortable: true,
			tooltip: 'Siege'
		},
		{
			key: 'gamesLost',
			label: 'L',
			sortable: true,
			tooltip: 'Niederlagen'
		},
		{
			key: 'gamesWonInOt',
			label: 'OTW',
			sortable: true,
			tooltip: 'Siege (n.V.)'
		},
		{
			key: 'gamesLostInOt',
			label: 'OTL',
			sortable: true,
			tooltip: 'Niederlagen (n.V.)'
		},
		{
			key: 'goalDifference',
			label: 'GD',
			sortable: true,
			sortFunction: (a: HDStandingsResponseRow) => parseInt(a.goalDifference),
			tooltip: 'Tordifferenz'
		},
		{
			key: 'points',
			label: 'PTS',
			sortable: true,
			tooltip: 'Punkte'
		}
	];

	function getRows(standings: HDStandingsResponseData) {
		return standings
			? standings.rows.map((team) => ({
					...team,
					id: team.id,
					highlight: team.id.toString() === teamId
				}))
			: [];
	}
</script>

{#await standings}
	<Placeholder text="Tabelle wird geladen" />
{:then standingsResponse}
	{@const standings = standingsResponse.data}

	<Table
		columns={standingsCols}
		rows={getRows(standings)}
		{title}
		onSelect={(row) => goto(convertSlug(season?.slug, `/saison/{slug}/teams/${row.id}`))}
	>
		{#if standings?.rows.length === 0}
			<div class="text-primary text-center">Keine Spiele gefunden</div>
		{/if}
		{#snippet data(row, col, getRowData)}
			{#if col.key === 'teamShortname'}
				<div class="flex flex-row items-center gap-1">
					<img
						src={getTeamImageUrl(row.id, season?.seasonId ?? '')}
						alt={row.teamShortname}
						class="mb-1 hidden aspect-square h-8 object-contain md:block"
					/>
					<span>
						{row.teamShortname}
					</span>
				</div>
			{:else}
				{getRowData(row, col.key)}
			{/if}
		{/snippet}
	</Table>
{:catch error}
	{error}
{/await}
